import { Fragment, type ReactNode } from 'react';

const MIN_TOKEN_LENGTH = 2;

/**
 * Fold a single character to its lowercase, accent-stripped form. Returns the
 * folded char only when it stays a single code point (true for Portuguese
 * accented letters like "é" -> "e"); otherwise keeps the original lowercased so
 * the folded string stays index-aligned with the source.
 */
function foldChar(char: string): string {
    const folded = char
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
    return folded.length === 1 ? folded : char.toLowerCase();
}

function fold(chars: string[]): string {
    return chars.map(foldChar).join('');
}

/**
 * Highlight the parts of `text` that match `query`, accent- and case-insensitively.
 *
 * Mirrors how the backend ranks results (accent-folded, per-word). Fuzzy/typo
 * matches that have no literal overlap (e.g. "autismo" -> "autista") simply render
 * without a highlight — the bolding is a hint, not the relevance signal itself.
 */
export function highlightMatch(text: string, query: string | undefined): ReactNode {
    const trimmed = query?.trim();
    if (!trimmed) return text;

    // Work in code points so folding stays index-aligned with the original text.
    const chars = Array.from(text);
    const foldedText = fold(chars);

    const tokens = Array.from(
        new Set(
            fold(Array.from(trimmed))
                .split(/\s+/)
                .filter((token) => token.length >= MIN_TOKEN_LENGTH),
        ),
    );
    if (tokens.length === 0) return text;

    // Collect [start, end) match ranges over the folded text.
    const ranges: Array<[number, number]> = [];
    for (const token of tokens) {
        let from = 0;
        let idx = foldedText.indexOf(token, from);
        while (idx !== -1) {
            ranges.push([idx, idx + token.length]);
            from = idx + token.length;
            idx = foldedText.indexOf(token, from);
        }
    }
    if (ranges.length === 0) return text;

    // Merge overlapping/adjacent ranges.
    ranges.sort((a, b) => a[0] - b[0]);
    const merged: Array<[number, number]> = [];
    for (const [start, end] of ranges) {
        const last = merged[merged.length - 1];
        if (last && start <= last[1]) {
            last[1] = Math.max(last[1], end);
        } else {
            merged.push([start, end]);
        }
    }

    const nodes: ReactNode[] = [];
    let cursor = 0;
    merged.forEach(([start, end], i) => {
        if (cursor < start) {
            nodes.push(<Fragment key={`t-${i}`}>{chars.slice(cursor, start).join('')}</Fragment>);
        }
        nodes.push(
            <mark key={`m-${i}`} className="bg-transparent font-semibold text-foreground">
                {chars.slice(start, end).join('')}
            </mark>,
        );
        cursor = end;
    });
    if (cursor < chars.length) {
        nodes.push(<Fragment key="t-end">{chars.slice(cursor).join('')}</Fragment>);
    }

    return nodes;
}
